import { debounce, FormControl, FormHelperText, FormLabel, Grid, SxProps, Theme, Tooltip } from "@mui/material";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { DateValidationError, PickerChangeHandlerContext, DatePicker as XDatePicker } from '@mui/x-date-pickers';
import { Info } from "@mui/icons-material";
import { fromEasternWallDate, toEasternWallDate } from "../../util/easternTime";

export interface IDatePickerProps {
    value?: Date | string | null;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    label?: string;
    onChange: (date?: Date | null) => void;
    errorText?: string;
    tooltip?: string;
    minDate?: Date | string;
    maxDate?: Date | string;
    sx?: SxProps<Theme> | undefined
};

// Shows and enters the Eastern calendar day on any device: the picker works on an Eastern "wall" Date, and the
// value passed to onChange is the instant of Eastern midnight on the picked day.
const toWall = (date?: Date | string | null) => (date ? toEasternWallDate(date) : null);

export const DatePicker: FunctionComponent<IDatePickerProps> = (props) => {
    const { value, disabled, required, error, label, onChange, errorText, tooltip, minDate, maxDate, sx } = props;
    const [fieldValue, setFieldValue] = useState<Date | null>(toWall(value));

    useEffect(() => {
        setFieldValue(toWall(value));
    }, [value]);

    const debouncedHandleChange = useMemo(
        () => debounce((date: Date | null | undefined, context: PickerChangeHandlerContext<DateValidationError>) => {
            if (!context.validationError) {
                if (date) {
                    onChange(fromEasternWallDate(new Date(date.getFullYear(), date.getMonth(), date.getDate())));
                } else {
                    onChange(date);
                }
            }
        }, 300)
        , [onChange]);

    return (
        <FormControl
            fullWidth
            disabled={disabled}
            required={required}
            error={error}>
            <Grid container spacing={2}>
                <Grid item>
                    <FormLabel>{label}</FormLabel>
                </Grid>
                {tooltip && <Grid item>
                    <Tooltip title={tooltip}><Info /></Tooltip>
                </Grid>}
            </Grid>
            <XDatePicker
                value={fieldValue}
                onChange={(value, context) => {
                    setFieldValue(value);
                    debouncedHandleChange(value, context);
                }}
                slotProps={{
                    textField: {
                        error: error
                    }
                }}
                minDate={toWall(minDate)}
                maxDate={toWall(maxDate)}
                disabled={disabled}
                sx={sx as any}
            />
            <FormHelperText>{errorText}</FormHelperText>
        </FormControl>
    );
};